import {styled} from "styled-components";
import type {MouseEvent} from "react";
import {cloneElement, useCallback, useEffect, useRef, useState} from "react";
import {Observer} from "mobx-react";
import {useAppStore} from "../../../StateManager";
import {Permission, XyiconFeature} from "../../../generated/api/base";
import type {ExportFormat} from "../../../data/exporters/BaseExporter";
import {ExporterFactory} from "../../../data/exporters/ExporterFactory";
import {Functions} from "../../../utils/function/Functions";
import type {IModel} from "../../../data/models/Model";
import {HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {ReactUtils} from "../../utils/ReactUtils";
import {ButtonV5} from "../button/ButtonV5";
import CirclePlusIcon from "../icons/circle-plus.svg?react";
import SlidersIcon from "../icons/sliders.svg?react";
import FilterIcon from "../icons/filter.svg?react";
import BrushIcon from "../icons/brush.svg?react";
import Columns3Icon from "../icons/columns-3.svg?react";
import DotsHorizontalIcon from "../icons/dots-horizontal.svg?react";
import ArrowDownToSquareIcon from "../icons/arrow-down-to-square.svg?react";
import FileArrowRightIcon from "../icons/file-arrow-right.svg?react";
import CloseIcon from "../icons/xmark.svg?react";
import {baseDistance, FlexCenter, FlexCenterStyle, fontSize, fontWeight, radius} from "../styles/styles";
import {ManageVersionSetsV5} from "../modules/space/versionset/ManageVersionSetsV5";
import {DropdownButtonV5} from "../interaction/DropdownButtonV5";
import {FeatureImportPanelV5} from "../abstract/view/FeatureImportPanelV5";
import {SearchFieldV5} from "../input/search/SearchFieldV5";
import {IconButtonStyled, IconButtonV5} from "../interaction/IconButtonV5";
import {colorPalette} from "../styles/colorPalette";
import {SelectInputStyled, SelectInputV5} from "../input/select/SelectInputV5";
import {ManageColumnsPanelV5} from "../abstract/table/ManageColumnsPanelV5";
import type {GridItem, GridSide} from "../modules/spaceeditor/spaceselector/GridTypes";
import type {IDropdownOption, ISubOptionWrapper} from "../interaction/DropdownOptionsV5";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import type {ViewChangeType} from "../../../data/models/ViewUtils";
import {AlertV5} from "../popup/AlertV5";
import {FilterEditorV5} from "../filter/FilterEditorV5";
import {useUnsavedLayout} from "../abstract/table/UnsavedLayoutV5";
import {useUnsavedFilters} from "../viewbar/FilterChangeNotificationV5";
import {useUnsavedSpotlight} from "../spotlight/useUnsavedSpotlight";
import {useSpotlightRules} from "../spotlight/useSpotlightRules";
import {SpotlightPopupV5} from "../spotlight/SpotlightPopupV5";
import {featureTitles} from "../../../data/state/AppStateConstants";
import {useCloseOnKeyboardPress} from "../utils";
import {doesUserHavePermissionToCreateCatalogItem} from "../../modules/catalog/create/CatalogUtils";
import {ViewSelectorV5} from "./ViewSelectorV5";

interface IActionBarV5Props {
	readonly feature: XyiconFeature;
	readonly items: IModel[];
	readonly createButtonRef: React.RefObject<HTMLDivElement>;
	readonly manageColumnsButtonRef: React.RefObject<HTMLDivElement>;
	readonly isSideGrid?: boolean;
	readonly filterCount: number;
	readonly onSearchChange: (value: string) => void;
	readonly create: (onClose?: () => void) => React.ReactElement<any, string | React.JSXElementConstructor<any>>;
	readonly onCloseCreate: () => void;
	readonly onResetFilters: () => void;
	readonly onSelect: (item: IModel[]) => void;
	readonly isLoadingList: boolean;
	readonly searchString: string;
	readonly isDetailsPanelOpen?: boolean;
	readonly closePopupsCallback?: (closeHandler: () => void) => void;
}

enum PopupType {
	None = 0,
	ManageVersions = 1,
	ManageColumns = 2,
	FilterPanel = 3,
	ImportPanel = 4,
	CreatePopup = 5,
	SpotlightPanel = 6,
}

export const ActionBarV5 = (props: IActionBarV5Props) => {
	const appState = useAppStore((state) => state.appState);
	const {
		feature,
		items,
		onSearchChange,
		isSideGrid,
		create,
		onSelect,
		createButtonRef,
		manageColumnsButtonRef,
		isLoadingList,
		filterCount,
		searchString,
		isDetailsPanelOpen,
		closePopupsCallback,
	} = props;
	const [openedPopupType, setOpenedPopupType] = useState<PopupType>(PopupType.None);

	useEffect(() => {
		if (closePopupsCallback) {
			closePopupsCallback(() => setOpenedPopupType(PopupType.None));
		}
	}, [closePopupsCallback]);
	const [shouldManageColumnsButtonMoveIntoMeatballMenu, setShouldManageColumnsButtonMoveIntoMeatballMenu] = useState<boolean>(false);
	const [shouldSpotlightButtonMoveIntoMeatballMenu, setShouldSpotlightButtonMoveIntoMeatballMenu] = useState<boolean>(false);
	const ref = useRef<HTMLDivElement>(null);
	const filterButtonRef = useRef<HTMLDivElement>(null);
	const saveViewBarRef = useRef<HTMLDivElement>(null);
	const spotlightButtonRef = useRef<HTMLDivElement>(null);
	const meatballMenuRef = useRef<HTMLDivElement>(null);
	const isAnyTypeNotHidden = appState.actions.hasAnyTypeTheGivenPermissionInModule(feature, Permission.Update);
	const canCreateOrImport = feature === XyiconFeature.XyiconCatalog ? doesUserHavePermissionToCreateCatalogItem(appState.user) : isAnyTypeNotHidden;
	const canManageVersions = feature === XyiconFeature.Space && isAnyTypeNotHidden;

	const selectedView = appState.actions.getSelectedView(isSideGrid ? XyiconFeature.SpaceEditor : feature);
	const [layoutChanges, setLayoutChanges] = useState<ViewChangeType[]>([]);
	const [filterChanges, setFilterChanges] = useState<ViewChangeType[]>([]);
	const [spotlightChanges, setSpotlightChanges] = useState<ViewChangeType[]>([]);

	useUnsavedLayout(selectedView, setLayoutChanges);
	useUnsavedFilters(selectedView, setFilterChanges);
	useUnsavedSpotlight(selectedView, setSpotlightChanges);

	// Initialize spotlight hooks for clearing rules
	const listViewSpotlightRules = useSpotlightRules({
		feature: isSideGrid ? XyiconFeature.SpaceEditor : feature,
		appState,
		ruleType: "listViewSpotlightRules",
	});

	const xyiconSpotlightRules = useSpotlightRules({
		feature: isSideGrid ? XyiconFeature.SpaceEditor : XyiconFeature.Xyicon,
		appState,
		ruleType: "xyiconSpotlightRules",
	});

	const boundarySpotlightRules = useSpotlightRules({
		feature: isSideGrid ? XyiconFeature.SpaceEditor : XyiconFeature.Boundary,
		appState,
		ruleType: "boundarySpotlightRules",
	});

	const togglePopup = (popupType: PopupType) => {
		if (openedPopupType === popupType) {
			setOpenedPopupType(PopupType.None);
		} else {
			setOpenedPopupType(popupType);
		}
	};

	const closePopup = useCallback(() => {
		setOpenedPopupType(PopupType.None);
	}, []);

	const onResetFiltersClick = (event: MouseEvent) => {
		event.stopPropagation();
		props.onResetFilters();
	};

	const onResetSpotlightClick = (event: MouseEvent) => {
		event.stopPropagation();
		if (isSideGrid) {
			xyiconSpotlightRules.clearAllRules();
			boundarySpotlightRules.clearAllRules();
		} else {
			listViewSpotlightRules.clearAllRules();
		}
	};

	useEffect(() => {
		if (ref.current) {
			const resizeObserver = new ResizeObserver((entries) => {
				for (const entry of entries) {
					const width = entry.contentRect.width;
					setShouldManageColumnsButtonMoveIntoMeatballMenu(width < 550);
					setShouldSpotlightButtonMoveIntoMeatballMenu(width < 650);
				}
			});

			resizeObserver.observe(ref.current);

			return () => resizeObserver.disconnect();
		}
	}, []);

	useCloseOnKeyboardPress(closePopup, KeyboardListener.KEY_ESCAPE, [openedPopupType !== PopupType.None], {
		enabled: openedPopupType !== PopupType.None,
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});

	const onExport = (type: ExportFormat) => {
		const exporter = ExporterFactory.createExporter(type, appState);

		if (exporter) {
			exporter.exportView(appState.actions.getViewById(appState.selectedViewId[feature]), items);
		}
	};

	const onCloseCreate = () => {
		props.onCloseCreate?.();
		closePopup();
	};

	const onCreateClick = () => {
		if (XyiconFeature.Xyicon === feature) {
			create();
		} else if (create) {
			setOpenedPopupType(PopupType.CreatePopup);
		}
	};

	const checkAreThereAnyActiveSpotlightRules = () => {
		const spotlightRules = selectedView?.additionalDetails?.spotlightRules;
		let totalSpotlightRulesCount = 0;
		if (spotlightRules) {
			if (isSideGrid) {
				totalSpotlightRulesCount = (spotlightRules.xyiconSpotlightRules?.length || 0) + (spotlightRules.boundarySpotlightRules?.length || 0);
			} else {
				totalSpotlightRulesCount = spotlightRules.listViewSpotlightRules?.length || 0;
			}
		}
		return totalSpotlightRulesCount > 0;
	};

	const dropDownOptions: (IDropdownOption | ISubOptionWrapper)[] = [
		{
			label: "Export",
			IconComponent: FileArrowRightIcon,
			options: [
				{
					onClick: () => onExport("excel"),
					label: "Export to Excel",
				},
				{
					onClick: () => onExport("csv"),
					label: "Export to CSV",
				},
			],
		},
	];

	if (canCreateOrImport) {
		dropDownOptions.push({
			label: "Import",
			onClick: () => setOpenedPopupType(PopupType.ImportPanel),
			IconComponent: ArrowDownToSquareIcon,
		});
	}

	if (shouldSpotlightButtonMoveIntoMeatballMenu) {
		dropDownOptions.unshift({
			label: "Spotlight",
			IconComponent: BrushIcon,
			onClick: () => togglePopup(PopupType.SpotlightPanel),
			isActive: checkAreThereAnyActiveSpotlightRules(),
		});
	}

	if (shouldManageColumnsButtonMoveIntoMeatballMenu) {
		dropDownOptions.unshift({
			label: "Manage Columns",
			IconComponent: Columns3Icon,
			onClick: () => setOpenedPopupType(PopupType.ManageColumns),
		});
	}

	return (
		<Observer>
			{() => {
				const selectedView = appState.actions.getSelectedView(isSideGrid ? XyiconFeature.SpaceEditor : feature);
				const areThereActiveFilters = props.filterCount > 0;
				const allChanges: ViewChangeType[] = [...layoutChanges, ...filterChanges, ...spotlightChanges];
				const hasChanges =
					selectedView.hasUnsavedChanges("columns", false) ||
					selectedView.hasUnsavedChanges("column sorts", false) ||
					selectedView.hasUnsavedChanges("filters", false) ||
					selectedView.hasUnsavedChanges("spotlight", false);

				const areThereActiveSpotlightRules = checkAreThereAnyActiveSpotlightRules();

				return (
					<>
						<ActionBarV5Styled ref={ref}>
							<FlexCenter
								$gap={baseDistance.sm}
								data-cy="action-bar-left-section-container"
							>
								{isSideGrid && (
									<SideBarGridItemTypeSelectorStyled>
										<SelectInputV5
											options={["Xyicon List", "Boundary List"]}
											selected={appState.app.graphicalTools.gridItemSide.toLowerCase().includes("boundary") ? "Boundary List" : "Xyicon List"}
											onChange={(option: string) => {
												const side: GridSide = appState.app.graphicalTools.gridItemSide.includes("left") ? "left" : "right";
												appState.app.graphicalTools.gridItemSide = option.toLowerCase().includes("boundary") ? `${side}-boundary` : `${side}-xyicon`;
											}}
										/>
									</SideBarGridItemTypeSelectorStyled>
								)}
								{[XyiconFeature.Portfolio, XyiconFeature.XyiconCatalog, XyiconFeature.Space].includes(feature) && (
									<ViewSelectorV5 feature={feature} />
								)}
								{feature == XyiconFeature.Report && (
									<>
										<div className="moduleName">REPORTS</div>
										<LineStyled />
									</>
								)}
								<SearchFieldV5
									value={searchString}
									onInput={onSearchChange}
									minimized={true}
									cyId={`search-field-${featureTitles[feature]}`}
								/>
								{feature !== XyiconFeature.Report && !isSideGrid && (
									<FilterButtonStyled
										ref={filterButtonRef}
										className={ReactUtils.cls("FilterButtonStyled", {
											isActive: openedPopupType === PopupType.FilterPanel || areThereActiveFilters,
											isBold: areThereActiveFilters,
										})}
										onClick={() => togglePopup(PopupType.FilterPanel)}
										data-cy="action-bar-filter-button"
									>
										<FilterIcon />
										<div className="label">{filterCount ? filterCount.toString() : "Filter"}</div>
										{areThereActiveFilters && (
											<IconButtonV5
												IconComponent={CloseIcon}
												onClick={onResetFiltersClick}
											/>
										)}
									</FilterButtonStyled>
								)}
								{canManageVersions && (
									<ButtonV5
										onClick={() => setOpenedPopupType(PopupType.ManageVersions)}
										label="Manage Versions"
										type="tertiary"
									>
										<SlidersIcon />
									</ButtonV5>
								)}
								{isSideGrid && (
									<SideBarGridItemSideSelectorStyled>
										<SelectInputV5
											options={["Table Left", "Table Right"]}
											selected={appState.app.graphicalTools.gridItemSide.includes("left") ? "Table Left" : "Table Right"}
											onChange={(option: string) => {
												const gridItem: GridItem = appState.app.graphicalTools.gridItemSide.includes("boundary") ? "boundary" : "xyicon";
												const newGridSide: GridSide = option.toLowerCase().includes("left") ? "left" : "right";

												appState.app.graphicalTools.gridItemSide = `${newGridSide}-${gridItem}`;
											}}
										/>
									</SideBarGridItemSideSelectorStyled>
								)}
								{!shouldManageColumnsButtonMoveIntoMeatballMenu &&
									[XyiconFeature.Portfolio, XyiconFeature.XyiconCatalog, XyiconFeature.Boundary, XyiconFeature.Xyicon].includes(feature) && (
										<ManageColumnButtonStyled
											className={ReactUtils.cls("ManageColumnStyled", {isActive: openedPopupType === PopupType.ManageColumns})}
											data-cy="action-bar-manage-columns-button"
										>
											<ButtonV5
												onClick={() => setOpenedPopupType(PopupType.ManageColumns)}
												label="Manage Columns"
												type="tertiary"
												ref={manageColumnsButtonRef}
											>
												<Columns3Icon />
											</ButtonV5>
										</ManageColumnButtonStyled>
									)}
								{!shouldSpotlightButtonMoveIntoMeatballMenu && ![XyiconFeature.Report, XyiconFeature.Space].includes(feature) && (
									<SpotlightButtonStyled
										ref={spotlightButtonRef}
										className={ReactUtils.cls("FilterButtonStyled", {
											isActive: openedPopupType === PopupType.SpotlightPanel || areThereActiveSpotlightRules,
											isBold: areThereActiveSpotlightRules,
										})}
										onClick={() => togglePopup(PopupType.SpotlightPanel)}
										data-cy="action-bar-spotlight-button"
									>
										<BrushIcon />
										<div>Spotlight</div>
										{areThereActiveSpotlightRules && (
											<IconButtonV5
												IconComponent={CloseIcon}
												onClick={onResetSpotlightClick}
											/>
										)}
									</SpotlightButtonStyled>
								)}
								{feature != XyiconFeature.Report && (
									<div ref={meatballMenuRef}>
										<DropdownButtonV5
											options={dropDownOptions}
											horizontalAlignment={HorizontalAlignment.left}
											button={
												<ExportDotsStyled
													IconComponent={DotsHorizontalIcon}
													onClick={Functions.emptyFunction}
												/>
											}
											dataCy="action-bar-more-options-button"
										/>
									</div>
								)}
							</FlexCenter>
							<FlexCenter $gap={baseDistance.sm}>
								{!isSideGrid && allChanges.length > 0 && hasChanges && (
									<AlertV5
										divRef={saveViewBarRef}
										feature={feature}
										type={allChanges.length > 0 ? allChanges : null}
										isSideGrid={isSideGrid}
										onClose={closePopup}
										isDetailsPanelOpen={isDetailsPanelOpen}
									/>
								)}
								{create && canCreateOrImport && (
									<ButtonV5
										onClick={onCreateClick}
										label="CREATE"
										ref={createButtonRef}
									>
										<CirclePlusIcon />
									</ButtonV5>
								)}
								{isSideGrid && (
									<IconButtonV5
										IconComponent={CloseIcon}
										title="Close"
										onClick={() => {
											appState.app.graphicalTools.gridItemSide = "";
										}}
									/>
								)}
							</FlexCenter>
						</ActionBarV5Styled>
						{canManageVersions && openedPopupType === PopupType.ManageVersions && <ManageVersionSetsV5 onClose={closePopup} />}
						{openedPopupType === PopupType.FilterPanel && (
							<FilterEditorV5
								onClose={closePopup}
								loader={isLoadingList}
								features={[feature]}
								feature={feature}
								items={items}
								onSelect={onSelect}
								parentRef={filterButtonRef}
							/>
						)}
						{openedPopupType === PopupType.SpotlightPanel && (
							<SpotlightPopupV5
								onClose={closePopup}
								feature={isSideGrid ? XyiconFeature.SpaceEditor : feature}
								items={items}
								parentRef={shouldSpotlightButtonMoveIntoMeatballMenu ? meatballMenuRef : spotlightButtonRef}
								verticalAlignment={VerticalAlignment.bottomOuter}
								horizontalAlignment={HorizontalAlignment.left}
								offsetX={0}
								offsetY={8}
								openInSpaceEditor={isSideGrid}
							/>
						)}
						{openedPopupType === PopupType.ManageColumns && (
							<ManageColumnsPanelV5
								feature={feature}
								view={selectedView}
								onClose={closePopup}
								parentRef={manageColumnsButtonRef}
							/>
						)}
						{openedPopupType === PopupType.ImportPanel && (
							<FeatureImportPanelV5
								onClose={closePopup}
								calledFromThisModule={props.feature}
								dataCyTitle="action-bar"
							/>
						)}
						{openedPopupType === PopupType.CreatePopup && canCreateOrImport && create && cloneElement(create(onCloseCreate))}
					</>
				);
			}}
		</Observer>
	);
};

const ActionBarV5Styled = styled.div`
	height: 64px;
	padding: 0 ${baseDistance.md};
	${FlexCenterStyle};
	justify-content: space-between;

	> ${FlexCenter} {
		& > ${SelectInputStyled} {
			border: none;
		}
	}

	.moduleName {
		font-weight: ${fontWeight.bold};
	}
`;

const LineStyled = styled.div`
	height: 32px;
	width: 1px;
	background-color: ${colorPalette.gray.c950};
	position: relative;
	left: 0;
	margin: ${baseDistance.sm};
`;

const ExportDotsStyled = styled(IconButtonV5)`
	svg {
		width: 16px;
		height: 16px;
	}
`;

const SideBarGridItemTypeSelectorStyled = styled.div`
	text-transform: uppercase;
	font-weight: ${fontWeight.bold};
	white-space: nowrap;

	${SelectInputStyled} {
		border: none;
	}
`;

const SideBarGridItemSideSelectorStyled = styled.div`
	white-space: nowrap;

	${SelectInputStyled} {
		border: none;
	}
`;

const FilterButtonStyled = styled.div`
	${FlexCenterStyle};
	cursor: pointer;
	border-radius: ${radius.sm};
	height: 32px;
	padding: ${baseDistance.sm};
	grid-gap: ${baseDistance.sm};
	font-size: ${fontSize.md};

	svg {
		width: 16px;
		height: 16px;
	}

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.isActive {
		background-color: ${colorPalette.primary.c200Light};
		color: ${colorPalette.primary.c500Primary};
	}

	&.isBold {
		.label {
			font-weight: bold;
		}
	}

	${IconButtonStyled} {
		background-color: inherit;
		min-width: 16px;
		height: 16px;
		width: 16px;
		height: 16px;
		margin: 0;

		svg {
			display: flex;
			justify-content: center;
		}
	}
`;
export const SpotlightButtonStyled = styled(FilterButtonStyled)``;

const ManageColumnButtonStyled = styled.div`
	cursor: pointer;
	border-radius: ${radius.sm};

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.isActive {
		background-color: ${colorPalette.primary.c200Light};

		.Button {
			color: ${colorPalette.primary.c500Primary};
		}
	}
`;
